<script setup lang="ts">
const { open, vacancy } = defineProps<{
  /**
   * Whether the modal is open.
   */
  open?: boolean
  /**
   * The vacancy to delete
   */
  vacancy: BaseVacancy
}>()

const emit = defineEmits(['close', 'deleted'])

const { t } = useI18n({
  useScope: 'local',
})

const closeModal = () => {
  emit('close')
}

const isDeleting = ref(false)

const router = useRouter()
const toaster = useToaster()

const { isVolunteer } = useCurrentOrganizationRoles()
const vacancyHomeRoute = computed(() => {
  return isVolunteer.value ? `/dashboard` : `/vacancies/`
})

const { handleServerError } = useServerErrorHandler()

const handleDelete = async () => {
  isDeleting.value = true
  try {
    // @ts-ignore // TODO: fix import, move vacancy client to app layer?
    await deleteVacancy(vacancy.id)
    closeModal()
    toaster.clearAll()
    toaster.show({
      title: t('success', typeLabelContext),
      message: t('deleteSuccessful', typeLabelContext),
      color: 'success',
      icon: 'ph:check',
      closable: true,
    })
    router.push(vacancyHomeRoute.value)
    emit('deleted')
  }
  catch (error) {
    handleServerError(error)
  }
  finally {
    isDeleting.value = false
  }
}

const typeLabelContext = useVacancyTypeName(vacancy)
</script>

<template>
  <TairoModal
    :open="open"
    size="sm"
    @close="closeModal"
  >
    <div class="mt-8 p-4 md:p-6">
      <div class="mx-auto w-full max-w-xs text-center">
        <h3
          class="font-heading text-muted-800 text-lg font-medium leading-6 dark:text-white"
        >
          {{ t('deleteTitle', typeLabelContext) }}
        </h3>

        <p
          class="font-alt text-muted-500 dark:text-muted-400 text-md mt-6 leading-5"
        >
          {{ t('deleteSubTitle', typeLabelContext) }}
        </p>
      </div>
    </div>

    <template #footer>
      <div class="p-4 md:p-6">
        <div class="flex gap-x-4">
          <BaseButton @click="closeModal">
            {{
              $t('buttonActions.cancel')
            }}
          </BaseButton>

          <BaseButton
            color="primary"
            flavor="solid"
            :loading="isDeleting"
            :disabled="isDeleting"
            @click="handleDelete"
          >
            {{ $t('buttonActions.delete') }}
          </BaseButton>
        </div>
      </div>
    </template>
  </TairoModal>
</template>

<i18n lang="json">
{
  "en": {
    "deleteTitle": "Delete {vacancyTypeName}",
    "deleteSubTitle": "Are you sure you want to delete this {vacancyTypeName}? this action cannot be reversed",
    "deleteSuccessful": "{vacancyTypeNameCapitalized} deleted successfully"
  },
  "nl": {
    "deleteTitle": "{vacancyTypeNameCapitalized} verwijderen",
    "deleteSubTitle": "Weet je zeker dat je deze {vacancyTypeName} wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt.",
    "deleteSuccessful": "{vacancyTypeNameCapitalized} succesvol verwijderd."
  }
}
</i18n>
